<template>
  <div class="p-password-reminder__form-container">
    <Loader v-if="isLoading"></Loader>
    <Alert
      v-if="!isLoading && errorMsgs.length"
      key="submit-errors"
      dismiss-by-native=""
      type="danger"
      :messages="errorMsgs"
      @close="removeErrors"></Alert>
    <form autocomplete="off" @submit.prevent="onSubmitForm">
      <div class="b-block b-block--transparent b-block--sm-pad">
        <!-- row -->
        <div class="form-floating">
          <input
            id="new-password"
            v-model.trim="newPassword"
            name="new-password"
            :type="passwordInputType"
            class="form-control"
            :placeholder="dictionary.newPasswordPlaceholder"
            :class="{ 'form-control--error': v$.newPassword.$error }"
            @blur="v$.newPassword.$touch" />
          <label for="new-password" class="form-label">
            {{ dictionary.newPasswordLabel }}
          </label>
          <button
            class="form-floating__btn-password"
            :class="showPassword ? 'form-floating__btn-password--seen' : ''"
            type="button"
            @click="() => (showPassword = !showPassword)"></button>

          <!-- validations -->
          <template v-if="v$.newPassword.$error">
            <div
              v-if="v$.newPassword.required.$invalid"
              class="field-validation-error">
              {{ dictionary.newPasswordRequired }}
            </div>
            <div
              v-if="v$.newPassword.regex.$invalid"
              class="field-validation-error">
              {{ dictionary.newPasswordRegex }}
            </div>
          </template>
        </div>

        <!-- row -->
        <div class="form-floating">
          <input
            id="new-password-confirm"
            v-model.trim="newPasswordConfirm"
            name="new-password-confirm"
            type="password"
            class="form-control"
            :placeholder="dictionary.newPasswordConfirmPlaceholder"
            :class="{ 'form-control--error': v$.newPasswordConfirm.$error }"
            @blur="v$.newPasswordConfirm.$touch"
            @paste="(e) => e.preventDefault()"
            @drop="(e) => e.preventDefault()" />

          <label for="new-password-confirm" class="form-label">
            {{ dictionary.newPasswordConfirmLabel }}
          </label>

          <!-- validations -->
          <template v-if="v$.newPasswordConfirm.$error">
            <div
              v-if="v$.newPasswordConfirm.sameAsNewPassword.$invalid"
              class="field-validation-error">
              {{ dictionary.newPasswordConfirmMatch }}
            </div>
          </template>
        </div>

        <div class="b-info-message">
          <div
            class="b-info-message__content"
            v-html="dictionary.passwordInfo"></div>
        </div>

        <div class="p-contact__form-message">
          {{ dictionary.fieldRequired }}
        </div>
      </div>

      <div class="b-block__form-btn">
        <button
          type="submit"
          class="p-contact__submit-btn"
          :disabled="submitButtonIsDisabled">
          <span>{{ dictionary.buttonSubmitText }}</span>
        </button>
      </div>
    </form>

    <ModalOverlay
      v-model="showSuccessModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.submitSuccessHeader }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div
            class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ dictionary.submitSuccessTitle }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ dictionary.submitSuccessContent }}
            </div>
            <button
              type="button"
              class="b-pop-up__alert-success__cta"
              @click="() => toggleSuccessModal(false)">
              {{ dictionary.submitSuccessClose }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import constants from '../../lib/constants'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import ModalOverlay from './../components/ModalOverlay.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'

const { password } = constants.regex

import { helpers, required, sameAs } from '@vuelidate/validators'

export default {
  components: { Loader, ModalOverlay, Alert },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showSuccessModal: false,
      showPassword: false,
      // change password
      newPassword: '',
      newPasswordConfirm: '',
    }
  },
  validations() {
    return {
      // change password
      newPassword: {
        required,
        regex: helpers.regex(password),
      },
      newPasswordConfirm: {
        sameAsNewPassword: sameAs(this.newPassword),
      },
    }
  },

  computed: {
    passwordInputType() {
      return this.showPassword ? 'text' : 'password'
    },
    newPasswordHasValue() {
      return typeof this.newPassword === 'string' && this.newPassword.length > 0
    },
    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
  },

  mounted() {
    lib.checkCaptcha(() => (this.isLoading = false))
  },

  methods: {
    removeErrors() {
      this.errorMsgs = []
    },
    toggleSuccessModal(show) {
      this.showSuccessModal = show
      if (!show) {
        location.refresh()
      }
    },

    resetDefaultFormData() {
      document.activeElement.blur()
      this.v$.$reset()
      this.newPassword = ''
      this.newPasswordConfirm = ''
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        resetCode: this.configuration.resetCode,
        memberId: this.configuration.memberId,
        newPassword: this.newPassword,
        newPasswordConfirm: this.newPasswordConfirm,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
      this.toggleSuccessModal(true)
      this.resetDefaultFormData()
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },
  },
}
</script>
